import { useQuery } from "@tanstack/react-query";
import {
  estimateActionCost,
  type ActionCostEstimate,
} from "@/services/billingApi";
import { useBillableAccounts } from "@/hooks/useBillableAccounts";

/**
 * Pre-submit cost estimate for a LoRA character-training run (power#916, W4
 * v1.6 — R6 dispute mitigation: a previously-free action is now charged, so
 * the wizard MUST show the cost before the user commits).
 *
 * Training is priced flat per run (`fixed`, cost_kind "known") against the
 * dedicated `lora-training` catalog action. There is no workflow (or training
 * run) at estimate time, so the item carries `surface: "lora_training"` with
 * the LoRA id as the subject — the backend authorizes via the LoRA read check
 * instead of the per-workflow gate.
 *
 * Charged against the user's active billing account (same resolution as the
 * node editor's NodeRunEstimate).
 */

/** `_service.actions.id` of the lora-training billing anchor (db 20260723000000). */
export const LORA_TRAINING_ACTION_ID = 41;

interface UseLoraTrainingCostEstimateResult {
  /** Flat per-run cost in credits; null while unavailable. */
  estimatedCredits: number | null;
  isLoading: boolean;
  /** True once a real estimate has been fetched (see useActionCostEstimate). */
  isAvailable: boolean;
  error: Error | null;
}

export function useLoraTrainingCostEstimate(
  loraId: string | null | undefined,
  enabled = true,
): UseLoraTrainingCostEstimateResult {
  const { activeAccount } = useBillableAccounts();
  const subscriptionId = activeAccount?.subscriptionId ?? null;

  const canQuery = enabled && !!loraId && !!subscriptionId;

  const query = useQuery({
    queryKey: ["lora-training-cost-estimate", loraId, subscriptionId],
    queryFn: async (): Promise<ActionCostEstimate | null> => {
      const results = await estimateActionCost({
        subscriptionId: subscriptionId as string,
        items: [
          {
            clientRef: `lora-training-${loraId}`,
            subjectEntityType: "workflow_node_instance",
            subjectEntityId: loraId as string,
            actionId: LORA_TRAINING_ACTION_ID,
            surface: "lora_training",
          },
        ],
      });
      return results[0] ?? null;
    },
    enabled: canQuery,
    staleTime: 60_000,
    retry: false,
  });

  return {
    estimatedCredits: query.data?.estimatedCredits ?? null,
    isLoading: canQuery && query.isLoading,
    isAvailable: query.isSuccess && query.data?.estimatedCredits != null,
    error: (query.error as Error | null) ?? null,
  };
}
